import { Point2D, ToolType } from '../../../types';

/**
 * 渲染临时绘制点（正在绘制中）
 */
export function renderTempPoints(
  ctx: CanvasRenderingContext2D,
  points: Point2D[],
  toolType: ToolType,
  wallThickness: number = 200,
  zoom: number = 1
): void {
  if (points.length === 0) return;

  // 固定像素大小，然后乘以 1/zoom 抵消 Canvas 的缩放
  // 当 zoom=1 时保持固定大小，当 zoom 变化时保持这个大小不变
  const scaleFactor = 1 / zoom;
  const fixedOuterRadius = 5 * scaleFactor;   // 外圈直径10像素
  const fixedInnerRadius = 4 * scaleFactor;  // 内圈直径8像素
  const fixedLineWidth = 2 * scaleFactor;    // 线宽2像素

  ctx.save();

  switch (toolType) {
    case ToolType.WALL:
      renderWallPreview(ctx, points, { thickness: wallThickness || 200, material: 'concrete' }, zoom);
      break;
    case ToolType.AREA:
      renderAreaPreview(ctx, points, fixedOuterRadius, fixedInnerRadius, fixedLineWidth);
      break;
    case ToolType.RECTANGLE:
      renderRectanglePreview(ctx, points, fixedOuterRadius, fixedInnerRadius, fixedLineWidth);
      break;
    case ToolType.CIRCLE:
      renderCirclePreview(ctx, points, fixedOuterRadius, fixedInnerRadius, fixedLineWidth);
      break;
    case ToolType.POLYGON:
      renderPolygonPreview(ctx, points, fixedOuterRadius, fixedInnerRadius, fixedLineWidth);
      break;
    case ToolType.STAIRS:
      renderStairPreview(ctx, points, fixedOuterRadius, fixedInnerRadius, fixedLineWidth);
      break;
    default:
      renderDefaultPreview(ctx, points, fixedOuterRadius, fixedInnerRadius, fixedLineWidth);
  }

  ctx.restore();
}

/**
 * 墙面预览
 */
function renderWallPreview(
  ctx: CanvasRenderingContext2D,
  points: Point2D[],
  wallProperties: { thickness: number; material: string },
  zoom: number
): void {
  if (points.length < 1) return;

  // 根据材质获取颜色
  const materialColors: Record<string, { main: string; stroke: string }> = {
    concrete: { main: '#a8a29e', stroke: '#78716c' },
    brick: { main: '#b45309', stroke: '#92400e' },
    wood: { main: '#a16207', stroke: '#854d0e' },
    glass: { main: '#bae6fd', stroke: '#7dd3fc' },
    metal: { main: '#94a3b8', stroke: '#64748b' },
    drywall: { main: '#f5f5f4', stroke: '#d6d3d1' },
  };

  const colors = materialColors[wallProperties.material] || materialColors.concrete;

  // 固定像素大小，然后乘以 1/zoom 保持固定像素大小
  const scaleFactor = 1 / zoom;
  const outerRadius = 5 * scaleFactor;
  const innerRadius = 4 * scaleFactor;
  const baseLineWidth = wallProperties.thickness / 50;
  const lineWidth = baseLineWidth * scaleFactor;

  // 先绘制浅色描边
  ctx.strokeStyle = colors.stroke; // 根据材质调整描边颜色
  ctx.lineWidth = lineWidth + 4 * scaleFactor;
  ctx.lineCap = 'butt'; // 直角端点
  ctx.lineJoin = 'miter';
  ctx.globalAlpha = 0.6; // 降低不透明度

  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();

  // 绘制主线条
  ctx.strokeStyle = colors.main; // 根据材质调整主颜色
  ctx.lineWidth = lineWidth;
  ctx.globalAlpha = 1;

  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();

  // 绘制点（绿色）
  renderPoints(ctx, points, '#10b981', outerRadius, innerRadius);
}

/**
 * 区域预览
 */
function renderAreaPreview(ctx: CanvasRenderingContext2D, points: Point2D[], outerRadius: number, innerRadius: number, lineWidth: number): void {
  if (points.length < 1) return;

  // 如果有3个以上的点，绘制填充预览
  if (points.length >= 3) {
    ctx.fillStyle = '#bed1fe'; // 淡蓝色
    ctx.globalAlpha = 0.2;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // 绘制边框
  ctx.strokeStyle = '#bed1fe'; // 淡蓝色
  ctx.lineWidth = lineWidth;
  ctx.setLineDash([5, 5]);
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();

  ctx.setLineDash([]);

  // 绘制点
  renderPoints(ctx, points, '#bed1fe', outerRadius, innerRadius); // 淡蓝色
}

/**
 * 矩形预览
 */
function renderRectanglePreview(ctx: CanvasRenderingContext2D, points: Point2D[], outerRadius: number, innerRadius: number, lineWidth: number): void {
  if (points.length < 2) return;

  const p1 = points[0];
  const p2 = points[1];
  const width = p2.x - p1.x;
  const height = p2.y - p1.y;

  ctx.fillStyle = '#f59e0b';
  ctx.globalAlpha = 0.3;
  ctx.fillRect(p1.x, p1.y, width, height);
  ctx.globalAlpha = 1;

  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = lineWidth;
  ctx.strokeRect(p1.x, p1.y, width, height);

  // 绘制点
  renderPoints(ctx, points, '#f59e0b', outerRadius, innerRadius);
}

/**
 * 圆形预览
 */
function renderCirclePreview(ctx: CanvasRenderingContext2D, points: Point2D[], outerRadius: number, innerRadius: number, lineWidth: number): void {
  if (points.length < 2) return;

  const center = points[0];
  const edge = points[1];
  const radius = Math.sqrt(
    Math.pow(edge.x - center.x, 2) + Math.pow(edge.y - center.y, 2)
  );

  ctx.fillStyle = '#f59e0b';
  ctx.globalAlpha = 0.3;
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = lineWidth;
  ctx.stroke();

  // 绘制点
  renderPoints(ctx, points, '#f59e0b', outerRadius, innerRadius);
}

/**
 * 多边形预览
 */
function renderPolygonPreview(ctx: CanvasRenderingContext2D, points: Point2D[], outerRadius: number, innerRadius: number, lineWidth: number): void {
  if (points.length < 2) return;

  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = lineWidth;
  ctx.setLineDash([5, 5]);
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();

  ctx.setLineDash([]);

  // 如果有3个以上的点，绘制填充预览
  if (points.length >= 3) {
    ctx.fillStyle = '#f59e0b';
    ctx.globalAlpha = 0.2;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // 绘制点
  renderPoints(ctx, points, '#f59e0b', outerRadius, innerRadius);
}

/**
 * 默认预览
 */
function renderDefaultPreview(ctx: CanvasRenderingContext2D, points: Point2D[], outerRadius: number, innerRadius: number, lineWidth: number): void {
  if (points.length < 1) return;

  ctx.strokeStyle = '#666666';
  ctx.lineWidth = lineWidth;
  ctx.setLineDash([5, 5]);

  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();

  ctx.setLineDash([]);

  // 绘制点
  renderPoints(ctx, points, '#666666', outerRadius, innerRadius);
}

/**
 * 绘制点
 */
function renderPoints(ctx: CanvasRenderingContext2D, points: Point2D[], color: string, outerRadius: number, innerRadius: number): void {
  for (const point of points) {
    // 绘制外圈白色描边
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(point.x, point.y, outerRadius, 0, Math.PI * 2);
    ctx.fill();

    // 绘制主色
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(point.x, point.y, innerRadius, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * 楼梯预览
 */
function renderStairPreview(ctx: CanvasRenderingContext2D, points: Point2D[], outerRadius: number, innerRadius: number, lineWidth: number): void {
  if (points.length < 2) {
    renderPoints(ctx, points, '#ea580c', outerRadius, innerRadius);
    return;
  }

  const p1 = points[0];
  const p2 = points[1];
  const width = Math.abs(p2.x - p1.x);
  const height = Math.abs(p2.y - p1.y);

  ctx.fillStyle = 'rgba(234, 88, 12, 0.2)';
  ctx.fillRect(p1.x, p1.y, width, height);

  ctx.strokeStyle = '#ea580c';
  ctx.lineWidth = lineWidth;
  ctx.setLineDash([5, 5]);
  ctx.strokeRect(p1.x, p1.y, width, height);
  ctx.setLineDash([]);

  // 绘制阶梯线条
  const steps = 10;
  const stepHeight = height / steps;
  ctx.beginPath();
  for (let i = 1; i < steps; i++) {
    const y = p1.y + i * stepHeight;
    ctx.moveTo(p1.x, y);
    ctx.lineTo(p2.x, y);
  }
  ctx.stroke();

  renderPoints(ctx, points, '#ea580c', outerRadius, innerRadius);
}
